'use client';

import React, { useContext, useId, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ScreenSizeContext } from '@context';
import { Logout, WarningQld } from '@public/icons';
import {
	FileRetrieveResponse,
	FileUploadAPIError,
	FileUploadStatus,
	FileUploadStatusResponse,
	FileUploadValidationError,
	FileUploadValidationErrorMessage,
	UpdateAction,
} from '@types';
import { FileUploadItem, fileUploadMimeTypes, FileValidationSchema } from '@utils';
import { DropZone, FileDropItem, FileTrigger } from 'react-aria-components';
import { ValidationError } from 'yup';

import { ImageCarousel } from '../../imageCarousel/ImageCarousel';
import { AriaButton } from '../button/AriaButton';
import { Text } from '../typography/Typography';
import { UploadedFileList } from './UploadedFileList';

import styles from './fileUploadField.module.scss';

interface FileUploadFieldProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * The type of file to accept
	 */
	acceptedFileTypes?: 'image' | 'document' | 'all';
	/**
	 * The value of the file input
	 */
	value: FileUploadItem[];
	/**
	 * onChange function for keeping track field value
	 */
	onChange: (files: FileUploadItem[]) => void;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Boolean for determining whether this field allows multiple files to be selected
	 */
	allowsMultiple?: boolean;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/**
	 * Function to handle file upload
	 * @param file - The file to be uploaded
	 * @param setUploadingFiles - Function to update the state of uploading files
	 * @returns A promise that resolves when the upload is complete
	 */
	uploadFile: (
		file: File,
		abortSignal?: AbortSignal,
		timeout?: number,
		uploadStatusTimeout?: number,
	) => Promise<FileUploadStatusResponse>;
	/**
	 * Function to handle file retrieval
	 * @param file - The file to be retrieved
	 * @returns A promise that resolves when the retrieval is complete
	 */
	retrieveFile?: (fileIdentifier: string) => Promise<FileRetrieveResponse>;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
	customStyles,
	acceptedFileTypes = 'all',
	hintText,
	errorMsg,
	disabled = false,
	autoFocus,
	allowsMultiple = false,
	value = [],
	uploadFile,
	onChange,
	retrieveFile,
}) => {
	const internalId = useId();
	const [hintId] = useState(`${internalId}-hint`);
	const [errorId] = useState(`${internalId}-error`);
	const { isMobile } = useContext(ScreenSizeContext);
	const [files, setFiles] = React.useState<FileUploadItem[]>(value);
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);
	const abortControllersRef = useRef<Record<string, AbortController>>({});

	const uploadFileMutation = useMutation({
		mutationFn: (file: File) => {
			const abortController = new AbortController();
			abortControllersRef.current[file.name] = abortController;
			return uploadFile(file, abortController.signal);
		},
		onSuccess: (response, file) => {
			updateFileUploadStatus(file.name, response);
			delete abortControllersRef.current[file.name];
		},
		onError: (error: unknown, file: File) => {
			if (error instanceof Error && error.name !== 'AbortError') {
				handleUploadError(file.name, error);
			}
			delete abortControllersRef.current[file.name];
		},
	});

	const updateFiles = (updatedFiles: FileUploadItem[]) => {
		setFiles(updatedFiles);
		onChange(updatedFiles);
	};

	const validateFile = async (file: File): Promise<string | undefined> => {
		try {
			await FileValidationSchema(acceptedFileTypes).validate(file);
			return undefined;
		} catch (error) {
			if (error instanceof ValidationError) {
				if (error.type === FileUploadValidationError.MAX_FILE_SIZE) {
					return FileUploadValidationErrorMessage.MAX_FILE_SIZE;
				} else if (error.type === FileUploadValidationError.INVALID_FILE_FORMAT) {
					return FileUploadValidationErrorMessage.INVALID_FILE_FORMAT;
				}
			}
			return FileUploadValidationErrorMessage.UNKNOWN_ERROR;
		}
	};

	const updateFileUploadStatus = (fileName: string, response: FileUploadStatusResponse) => {
		setFiles(prevFiles => {
			const updatedFiles = prevFiles.map(item =>
				item.file?.name === fileName
					? {
							...item,
							uploadStatus: response,
							filename: response.fileuri?.split('/').pop() || undefined,
							error: undefined,
							fileLocation: undefined,
						}
					: item,
			);
			onChange(updatedFiles);
			return updatedFiles;
		});
	};

	const handleUploadError = (fileName: string, error: unknown) => {
		setFiles(prevFiles => {
			const updatedFiles = prevFiles.map(item => {
				if (item.file?.name !== fileName) return item;

				const isErrorInstance = error instanceof Error;
				const errorMessage = isErrorInstance ? error.message : '';

				const uploadStatus = {
					status:
						errorMessage === FileUploadAPIError.MALWARE_DETECTED
							? FileUploadStatus.DIRTY
							: FileUploadStatus.FAILED,
					fileuri: null,
					contactid: null,
				};

				let errorType = FileUploadAPIError.UPLOAD_FAILED;
				if (isErrorInstance) {
					if (errorMessage === FileUploadAPIError.CONNECTION_TIMEOUT) {
						errorType = FileUploadAPIError.CONNECTION_TIMEOUT;
					} else if (errorMessage === FileUploadAPIError.MALWARE_DETECTED) {
						errorType = FileUploadAPIError.MALWARE_DETECTED;
					}
				}

				return {
					...item,
					uploadStatus,
					error: errorType,
				};
			});

			onChange(updatedFiles);
			return updatedFiles;
		});
	};

	const handleFilesChange = async (newFiles: File[]) => {
		// Clear existing files first when multiple files are not allowed
		if (!allowsMultiple) {
			updateFiles([]);
		}

		const uniqueNewFiles = newFiles.filter(
			newFile =>
				!files.some(
					existingFile =>
						existingFile.file?.name === newFile.name && existingFile.file?.size === newFile.size,
				),
		);

		if (uniqueNewFiles.length === 0) return;

		// For single file, only take the first file
		const filesToProcess = allowsMultiple ? uniqueNewFiles : [uniqueNewFiles[0]];

		const updatedFiles = [
			...(allowsMultiple ? files : []), // Only keep existing files if multiple is allowed
			...filesToProcess.map(file => ({
				file,
				error: undefined,
				action: UpdateAction.CREATE,
			})),
		];
		updateFiles(updatedFiles);

		for (const newFile of filesToProcess) {
			const validationError = await validateFile(newFile);

			if (validationError === undefined) {
				updateFileUploadStatus(newFile.name, {
					status: FileUploadStatus.REQUESTED,
					fileuri: null,
					contactid: null,
				});
				uploadFileMutation.mutate(newFile);
			} else {
				setFiles(prevFiles => {
					const updatedFiles = prevFiles.map(item =>
						item.file?.name === newFile.name ? { ...item, error: validationError } : item,
					);
					onChange(updatedFiles);
					return updatedFiles;
				});
			}
		}
	};

	const handleRemove = (index: number) => {
		if (files) {
			const fileToRemove = files[index];
			if (fileToRemove.file) {
				const abortController = abortControllersRef.current[fileToRemove.file.name];
				if (abortController) {
					abortController.abort();
					delete abortControllersRef.current[fileToRemove.file.name];
				}
			}

			// For non-multiple uploads, just clear everything
			if (!allowsMultiple) {
				updateFiles([]);
				return;
			}

			// For multiple uploads, handle normally
			const updatedFiles = files
				.map((file, i) => {
					if (i === index) {
						if (file.uploadStatus?.status === FileUploadStatus.RESTORED) {
							// Mark restored files for removal
							return { ...file, action: UpdateAction.REMOVE };
						}
						// Remove files with other statuses
						return null;
					}
					return file;
				})
				.filter((file): file is FileUploadItem => file !== null);

			updateFiles(updatedFiles);
		}
	};

	const handleRestore = (file: FileUploadItem) => {
		if (file.action === UpdateAction.REMOVE) {
			const updatedFiles = files.map(item =>
				item === file ? { ...item, action: UpdateAction.NONE } : item,
			);
			updateFiles(updatedFiles);
		}
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			<div className={styles.textContainer}>
				{hintText && (
					<div>
						<Text variant="muted" id={hintId}>
							{hintText}
						</Text>
					</div>
				)}
				{hasError && (
					<div className={styles.errorContainer}>
						<Text variant="error" id={errorId} role="alert" className={styles.errorContent}>
							<WarningQld customStyles={styles.errorIcon} /> {errorMsg}
						</Text>
					</div>
				)}
			</div>
			<DropZone
				className={`${styles.dropzone} ${hasError ? styles['dropzone-error'] : ''}`}
				isDisabled={disabled}
				onDrop={async e => {
					try {
						const fileItems = e.items.filter(file => file.kind === 'file') as FileDropItem[];
						const resolvedFiles = (
							await Promise.all(
								fileItems.map(file =>
									file.getFile().catch(() => null),
								),
							)
						).filter((f): f is File => f !== null && f.type !== '' && f.size > 0);
						if (resolvedFiles.length > 0) {
							handleFilesChange(resolvedFiles);
						}
					} catch {
						// Silently ignore unsupported drops (e.g. folders)
					}
				}}
				aria-labelledby="file-upload-hint"
				aria-describedby="file-upload-instructions"
			>
				{({ isDisabled, isDropTarget }) => (
					<>
						{!isMobile && (
							<div
								className={[
									styles['upload-icon-wrapper'],
									isDropTarget && styles['upload-icon-wrapper-hover'],
									hasError && styles['upload-icon-wrapper-error'],
								].join(' ')}
							>
								<Logout
									customStyles={styles['upload-icon']}
									disabled={isDisabled}
									error={hasError}
									aria-hidden="true"
								/>
							</div>
						)}
						<p
							className={[
								styles['dropzone-text'],
								isDisabled && styles['dropzone-text-disabled'],
							].join(' ')}
							id="file-upload-instructions"
						>
							{isMobile
								? 'Select files to upload'
								: 'Drag and drop files here or select files to upload'}
						</p>
						<p
							className={[
								styles['dropzone-hint'],
								isDisabled && styles['dropzone-hint-disabled'],
							].join(' ')}
						>
							{acceptedFileTypes === 'image'
								? 'You can upload jpg, jpeg, and png files.'
								: acceptedFileTypes === 'document'
									? 'You can upload doc, docx, pdf, and txt files.'
									: 'You can upload doc, docx, pdf, txt, jpg, jpeg, and png files.'}
							<br />
							Files can&apos;t be larger than 20 MB.
							<br />
							You can upload up to {allowsMultiple ? '10 files' : '1 file'}.
						</p>
						<FileTrigger
							allowsMultiple={allowsMultiple}
							acceptedFileTypes={fileUploadMimeTypes[acceptedFileTypes]}
							onSelect={(e: FileList | null) => {
								if (e) {
									handleFilesChange(Array.from(e));
								}
							}}
						>
							<AriaButton
								variant="secondary"
								size="large"
								className={styles.button}
								autoFocus={autoFocus}
								isDisabled={isDisabled}
								aria-label="Select files to upload"
							>
								{allowsMultiple ? 'Select files' : 'Select a file'}
							</AriaButton>
						</FileTrigger>
					</>
				)}
			</DropZone>
			{files && (
				<div>
					<UploadedFileList
						files={files}
						disabled={disabled}
						handleRemove={handleRemove}
						handleRestore={handleRestore}
						uploadFileMutation={uploadFileMutation}
						updateFileUploadStatus={updateFileUploadStatus}
					/>
					<ImageCarousel files={files} updateFiles={updateFiles} retrieveFile={retrieveFile} />
				</div>
			)}
		</div>
	);
};
